import { useApiClient } from '@brewardapp/api-client';
import { Button, Card, Loader, ScoresheetSection, Tabs } from '@breward/ui';
import qs from 'qs';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useMemo, useEffect } from 'react';
import ComparisonOverview from './components/ComparisonOverview/ComparisonOverview';
import SectionComparisonView from './components/SectionComparisonView/SectionComparisonView';
import FeedbackSection from './components/FeedbackSection/FeedbackSection';
import FlawsSection from './components/FlawsSection/FlawsSection';

interface ComparisonResult {
    compatibilityPercentage: number;
    differences: Array<{
        actual: any;
        details: any;
        expected: any;
        message: string;
        rule: string;
        ruleType: string;
        section: string;
        subsection: string;
    }>;
    passedChecks: number;
    totalChecks: number;
    sectionResults: Array<{
        differences: any[];
        passedChecks: number;
        section: string;
        totalChecks: number;
        subsectionResults: Array<{
            differences: any[];
            passedChecks: number;
            subsection: string;
            totalChecks: number;
        }>;
    }>;
}

interface ScoresheetData {
    id: string;
    name?: string;
    consensusScore?: number;
    values?: {
        disqualificationComment?: string;
        [key: string]: any;
    };
    entry?: {
        data?: {
            name?: string;
            contestsEdition?: {
                data?: {
                    order?: number;
                    contest?: {
                        data?: {
                            name?: string;
                        };
                    };
                    settings?: {
                        judging?: {
                            judgeDataVisibility?: string;
                            hidePointsOnScoresheet?: boolean;
                        };
                    };
                };
            };
        };
    };
    judge?: {
        data?: {
            user?: {
                data?: {
                    firstName?: string;
                    lastName?: string;
                };
            };
        };
    };
    style?: {
        data?: {
            name?: string;
            description?: string;
        };
    };
    scoresheetSchema?: {
        data?: {
            id?: string;
            name?: string;
            scoringEnabled?: boolean;
            sections?: Array<{
                id: string;
                key: string;
                label: string;
                order: number;
                subsections: Array<{
                    id: string;
                    key: string;
                    label: string;
                    order: number;
                    fields: Array<{
                        id: string;
                        key: string;
                        label: string;
                        order: number;
                        fieldType: string;
                        description: string;
                        settings: {
                            [key: string]: any;
                        };
                    }>;
                }>;
            }>;
        };
    };
    comparisonResult?: ComparisonResult;
    hasUnreadComments?: boolean;
}

const integerToRoman = (num: number): string => {
    if (!num) return '';
    const rules: { [key: string]: number } = {
        M: 1000, CM: 900, D: 500, CD: 400,
        C: 100, XC: 90, L: 50, XL: 40,
        X: 10, IX: 9, V: 5, IV: 4, I: 1
    };
    let res = '';
    for (const r in rules) {
        while (num >= rules[r]) {
            res += r;
            num -= rules[r];
        }
    }
    return res;
};

interface ScoresheetPageProps {
    viewMode?: 'participant' | 'judge';
}

const ScoresheetPage = ({ viewMode = 'participant' }: ScoresheetPageProps) => {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const [t] = useTranslation();
    const parsedQuery = qs.parse(location?.search, { ignoreQueryPrefix: true });
    const [currentTab, setCurrentTab] = useState<string>('feedback');

    const api = useApiClient();
    const {
        data: scoresheetData,
        isLoading,
        refetch,
    } = api.scoresheets.useFindScoresheet(
        {
            filter: {
                id: params?.scoresheetId,
            },
            include:
                'style,entry,entry.contests_edition,entry.contests_edition.contest,scoresheet_schema,comments,comments.user,judge,judge.user',
        },
        {
            enabled: !!params?.scoresheetId,
        },
    ) as { data: ScoresheetData | undefined; isLoading: boolean; refetch: () => void };

    const downloadScoresheet = api.scoresheets.useDownloadScoresheet();
    const markAsRead = api.scoresheets.useMarkScoresheetAsRead();

    useEffect(() => {
        if (scoresheetData?.hasUnreadComments && scoresheetData?.id) {
            markAsRead?.mutate({ id: scoresheetData.id }, {
                onError(error) {
                    console.error('Failed to mark scoresheet as read', error);
                },
            });
        }
    }, [scoresheetData?.hasUnreadComments, scoresheetData?.id]);

    const handleDownload = () => {
        if (params?.scoresheetId) {
            downloadScoresheet.mutate({ id: params.scoresheetId });
        }
    };

    const handleBackNavigation = () => {
        const backTo = parsedQuery?.backTo as string;
        if (backTo) {
            navigate(backTo);
        } else {
            navigate(-1);
        }
    };

    const comparisonResult = scoresheetData?.comparisonResult;

    const tabs = useMemo(() => {
        const t_arr: { id: string; label: string; icon?: string }[] = [];

        if (viewMode === 'judge') {
            t_arr.push({ id: 'feedback', label: t('Pages:ScoresheetPage.Feedback', 'Feedback'), icon: 'icon-[ph--chat-circle-text]' });
        }

        if (comparisonResult && Object.keys(comparisonResult).length > 0 && !scoresheetData?.values?.disqualificationComment) {
            t_arr.push({ id: 'overview', label: t('Pages:ScoresheetPage.Overview', 'Overview'), icon: 'icon-[ph--info]' });
        }

        scoresheetData?.scoresheetSchema?.data?.sections?.forEach(section => {
            t_arr.push({ id: section.key, label: section.label });
        });

        if (comparisonResult?.sectionResults && !scoresheetData?.values?.disqualificationComment) {
            t_arr.push({ id: 'comparison', label: t('Pages:ScoresheetPage.Comparison', 'Comparison'), icon: 'icon-[ph--git-diff]' });
        }

        if (scoresheetData?.values?.flaws?.length > 0) {
            t_arr.push({ id: 'flaws', label: t('Pages:ScoresheetPage.Flaws', 'Flaws'), icon: 'icon-[ph--warning-circle]' });
        }

        if (viewMode !== 'judge') {
            t_arr.push({ id: 'feedback', label: t('Pages:ScoresheetPage.Feedback', 'Feedback'), icon: 'icon-[ph--chat-circle-text]' });
        }

        return t_arr;
    }, [scoresheetData, comparisonResult, t, viewMode]);

    useEffect(() => {
        if (tabs.length > 0 && !tabs.find(tab => tab.id === currentTab)) {
            setCurrentTab(tabs[0].id);
        }
    }, [tabs, currentTab]);

    if (isLoading) {
        return <Loader />;
    }

    if (!scoresheetData) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="text-xl font-medium text-content-600 dark:text-content-400 mb-2">
                        {t('Pages:ScoresheetPage.NotFound', 'Scoresheet not found')}
                    </div>
                    <Button onClick={handleBackNavigation}>
                        {t('Pages:ScoresheetPage.GoBack', 'Go Back')}
                    </Button>
                </div>
            </div>
        );
    }

    // Format consensus score for display
    const formatConsensusScore = (score: number | undefined) => {
        if (score === undefined || score === null) return null;
        return Number(score).toFixed(1);
    };

    const consensusScore = scoresheetData?.consensusScore;
    const formattedScore = formatConsensusScore(consensusScore);
    const hidePoints = Boolean(
        scoresheetData?.entry?.data?.contestsEdition?.data?.settings?.judging?.hidePointsOnScoresheet,
    );
    const showJudgeInfo = scoresheetData?.entry?.data?.contestsEdition?.data?.settings?.judging?.judgeDataVisibility !== 'data_hidden';

    return (
        <div className="space-y-4">
            {/* Back Button */}
            <button
                type="button"
                onClick={handleBackNavigation}
                className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/25 bg-white/30 text-slate-700 shadow-lg shadow-black/5 backdrop-blur-xl transition-colors hover:bg-white/45 dark:border-content-700/45 dark:bg-content-900/35 dark:text-content-100 dark:hover:bg-content-900/55"
            >
                <span className="icon-[ph--arrow-left] h-5 w-5" />
            </button>

            {/* Title Section inside Card */}
            <Card className="col-span-1">
                <div className="col-span-12 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                    <div className="flex-1">
                        <h1 className="text-3xl font-bold font-header text-primary-900 dark:text-primary-100 mb-2">
                            {scoresheetData?.name || scoresheetData?.entry?.data?.name || t('Pages:ScoresheetPage.UntitledScoresheet', 'Untitled Scoresheet')}
                        </h1>

                        {showJudgeInfo && scoresheetData?.judge?.data?.user?.data && (
                            <div className="flex items-center gap-2 text-lg font-sans text-primary-600 dark:text-primary-400 mb-2">
                                <div className="icon-[ph--scales] h-5 w-5" />
                                <span>{scoresheetData.judge.data.user.data.firstName} {scoresheetData.judge.data.user.data.lastName}</span>
                            </div>
                        )}

                        <div className="flex items-center gap-2 text-xl font-medium font-sans text-primary-800 dark:text-primary-200 mb-2">
                            <div className="icon-[ph--trophy] h-6 w-6" />
                            <span>
                                {scoresheetData?.entry?.data?.contestsEdition?.data?.order ? `${integerToRoman(scoresheetData.entry.data.contestsEdition.data.order)}. ` : ''}
                                {scoresheetData?.entry?.data?.contestsEdition?.data?.contest?.data?.name}
                            </span>
                        </div>

                        <div className="flex items-center gap-2 text-lg font-sans text-accent-600 dark:text-accent-400 italic">
                            <div className="icon-[ph--beer-bottle] h-5 w-5" />
                            <span>{scoresheetData?.style?.data?.name}</span>
                        </div>
                    </div>

                    <div className="flex flex-col items-start lg:items-end gap-4">
                        {!hidePoints && formattedScore && (
                            <div className="font-sans flex items-center gap-2">
                                <span className="text-primary-600 dark:text-primary-400 text-sm font-medium">
                                    {t('Pages:ScoresheetPage.ConsensusScore', 'Consensus Score')}:
                                </span>
                                <div className="bg-primary-100 dark:bg-primary-800 text-primary-800 dark:text-primary-200 px-4 py-2 rounded-lg font-semibold text-xl">
                                    {formattedScore}
                                </div>
                            </div>
                        )}
                        <Button
                            type="primary"
                            onClick={handleDownload}
                            disabled={downloadScoresheet.isPending}
                        >
                            <div className='flex flex-row items-center justify-center gap-2'>
                                <div className="icon-[ph--download] h-5 w-5 shrink-0" />
                                <div>
                                    {downloadScoresheet.isPending
                                        ? t('Pages:ScoresheetPage.Downloading', 'Downloading...')
                                        : t('Pages:ScoresheetPage.DownloadPDF', 'Download PDF')
                                    }
                                </div>
                            </div>
                        </Button>
                    </div>
                </div>
            </Card>

            {/* Main Content */}
            <div className="space-y-8 mt-4">
                {scoresheetData?.values?.disqualificationComment && (
                    <div className="font-sans mt-6 p-4 bg-error-50 dark:bg-error-900/30 border border-error-200 dark:border-error-800 rounded-lg">
                        <div className="flex items-start gap-3">
                            <div className="text-error-500 dark:text-error-400 mt-1">
                                <div className="icon-[ph--warning-circle] h-5 w-5" />
                            </div>
                            <div>
                                <div className="font-medium text-error-800 dark:text-error-200 mb-1">
                                    {t('Pages:ScoresheetPage.Disqualified', 'Entry Disqualified')}
                                </div>
                                <div className="text-error-700 dark:text-error-300">
                                    {scoresheetData.values.disqualificationComment}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {!scoresheetData?.values?.disqualificationComment && (
                    <div className="mb-6 flex justify-start w-full overflow-x-auto hide-scrollbar">
                        <Tabs
                            tabs={tabs}
                            currentTab={currentTab}
                            onTabChange={(t) => setCurrentTab(t.id!)}
                            variant="pills"
                        />
                    </div>
                )}

                <div className="mt-8">
                    {currentTab === 'overview' && comparisonResult && Object.keys(comparisonResult).length > 0 && (
                        <ComparisonOverview comparisonResult={comparisonResult} />
                    )}

                    {scoresheetData?.scoresheetSchema?.data?.sections?.map((scoresheetSection) => {
                        if (currentTab === scoresheetSection.key) {
                            return (
                                <ScoresheetSection
                                    key={scoresheetSection?.key}
                                    scoresheet={scoresheetData}
                                    section={scoresheetSection}
                                    hideHeader={true}
                                    onChange={() => refetch()}
                                />
                            );
                        }
                        return null;
                    })}

                    {currentTab === 'comparison' && comparisonResult?.sectionResults && (
                        <div className="space-y-6">
                            {comparisonResult.sectionResults.map((sectionResult) => (
                                <SectionComparisonView
                                    key={sectionResult.section}
                                    sectionResult={sectionResult}
                                    scoresheetValues={scoresheetData?.values}
                                    scoresheetSchema={scoresheetData?.scoresheetSchema}
                                />
                            ))}
                        </div>
                    )}

                    {currentTab === 'flaws' && scoresheetData?.values?.flaws?.length > 0 && (
                        <FlawsSection scoresheet={scoresheetData} />
                    )}

                    {currentTab === 'feedback' && (
                        <FeedbackSection
                            scoresheet={scoresheetData}
                            onChange={() => refetch()}
                            readOnlyParam={typeof parsedQuery?.review === 'string' ? parsedQuery.review === 'true' : false}
                            readOnlyGrade={viewMode === 'judge'}
                            isTabContent={true}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export { ScoresheetPage };
export default ScoresheetPage; 
